import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';

const ROLES = ['beneficiary', 'coach', 'client', 'team'];
const ROLE_LABELS = { beneficiary: 'Company (Beneficiary)', coach: 'Coach', client: 'Client', team: 'Team Admin' };

export default function UserManager({ programId, scopedCompanies, allCompanyCount, allPrograms = [], allClients = [] }) {
  const [users, setUsers]           = useState([]);
  const [companies, setCompanies]   = useState([]);
  const [clients, setClients]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(null);
  const [search, setSearch]         = useState('');
  const [filterRole, setFilterRole] = useState('all');

  const [form, setForm] = useState({
    email: '', displayName: '', role: 'beneficiary',
    password: '', companyId: '', clientId: '', programIds: programId ? [programId] : [],
  });

  const functions = getFunctions(undefined, 'europe-west1');

  useEffect(() => {
    async function load() {
      try {
        const [userSnap, compSnap, clientSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'companies')),
          getDocs(collection(db, 'clients')),
        ]);
        setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setClients(clientSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ email: '', displayName: '', role: 'beneficiary', password: '', companyId: '', clientId: '', programIds: programId ? [programId] : [] });
    setError(null);
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      email: user.email, displayName: user.displayName, role: user.role,
      password: '', companyId: user.companyId || '', clientId: user.clientId || '',
      programIds: user.programIds || [],
    });
    setError(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.displayName || !form.role) {
      setError('Email, name and role are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingUser) {
        const fn = httpsCallable(functions, 'updateUser');
        await fn({ uid: editingUser.uid || editingUser.id, ...form });
        setUsers(prev => prev.map(u =>
          (u.uid || u.id) === (editingUser.uid || editingUser.id)
            ? { ...u, ...form }
            : u
        ));
        setSuccess(`${form.displayName} updated successfully.`);
      } else {
        const fn = httpsCallable(functions, 'createUser');
        const result = await fn({ ...form, password: form.password || 'Welcome2026!' });
        const newUser = { id: result.data.uid, uid: result.data.uid, ...form };
        setUsers(prev => [...prev, newUser]);
        setSuccess(`${form.displayName} created. Temporary password: ${form.password || 'Welcome2026!'}`);
      }
      setShowForm(false);
    } catch (err) {
      setError(err.message || 'Failed to save user.');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Deactivate ${user.displayName}? They will no longer be able to sign in.`)) return;
    try {
      const fn = httpsCallable(functions, 'deactivateUser');
      await fn({ uid: user.uid || user.id });
      setUsers(prev => prev.map(u =>
        (u.uid || u.id) === (user.uid || user.id) ? { ...u, disabled: true } : u
      ));
    } catch (err) { setError(err.message); }
  };

  // When no filter is active, scopedCompanies covers every company, so scoping is a no-op —
  // we still compute it the same way for consistency, but it only narrows results when the
  // parent has actually filtered down to a subset (e.g. a specific client/program/cohort).
  const isScopeActive = scopedCompanies && allCompanyCount != null && scopedCompanies.length < allCompanyCount;
  const scopedCompanyIds = new Set((scopedCompanies || []).map(c => c.id));
  const scopedCompanyNames = new Set((scopedCompanies || []).map(c => c.name));
  // Coaches in scope — by whether ANY of their companies (via coachId) falls in scope
  const scopedCoachIds = new Set((scopedCompanies || []).map(c => c.coachId).filter(Boolean));

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || u.role === filterRole;

    let matchScope = true;
    if (isScopeActive) {
      if (u.role === 'beneficiary') {
        matchScope = (u.companyId && scopedCompanyIds.has(u.companyId)) ||
                     (u.companyName && scopedCompanyNames.has(u.companyName));
      } else if (u.role === 'coach') {
        matchScope = scopedCoachIds.has(u.uid || u.id);
      }
      // team/client always visible regardless of company-level scope
    }
    return matchSearch && matchRole && matchScope;
  });

  if (loading) return <div className="gm-loading"><div className="gm-spinner" /></div>;

  return (
    <div className="gm-user-manager">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search users…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit', width: 200 }}
          />
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
            style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit' }}>
            <option value="all">All roles</option>
            {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </div>
        <button className="gm-btn gm-btn-primary" onClick={openCreate}>+ Add user</button>
      </div>

      {success && <div className="gm-alert" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#166534', fontSize: '0.85rem' }}>{success}</div>}
      {error && !showForm && <div className="gm-alert gm-alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* User form */}
      {showForm && (
        <div className="gm-config-card" style={{ marginBottom: 16 }}>
          <div className="gm-config-label">{editingUser ? `Edit — ${editingUser.displayName}` : 'Add new user'}</div>
          {error && <div style={{ color: '#dc2626', fontSize: '0.82rem', marginBottom: 8 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div className="gm-form-group">
              <label>Full name *</label>
              <input type="text" value={form.displayName} onChange={e => setForm(p => ({ ...p, displayName: e.target.value }))} placeholder="e.g. Ama Mensah" />
            </div>
            <div className="gm-form-group">
              <label>Email address *</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="e.g. ama@company.com" disabled={!!editingUser} />
            </div>
            <div className="gm-form-group">
              <label>Role *</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            {!programId && (
              <div className="gm-form-group">
                <label>Programme *</label>
                <select value={form.programIds[0] || ''} onChange={e => setForm(p => ({ ...p, programIds: e.target.value ? [e.target.value] : [] }))}>
                  <option value="">— Select programme —</option>
                  {allPrograms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            <div className="gm-form-group">
              <label>{editingUser ? 'New password (leave blank to keep current)' : 'Temporary password'}</label>
              <input type="text" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder={editingUser ? 'Leave blank to keep current' : 'Default: Welcome2026!'} />
            </div>

            {form.role === 'beneficiary' && (
              <div className="gm-form-group">
                <label>Company</label>
                <select value={form.companyId} onChange={e => setForm(p => ({ ...p, companyId: e.target.value }))}>
                  <option value="">— Select company —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {form.role === 'coach' && (
              <div className="gm-form-group">
                <label>Primary company (can update coach assignments separately)</label>
                <select value={form.companyId} onChange={e => setForm(p => ({ ...p, companyId: e.target.value }))}>
                  <option value="">— Select company —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {form.role === 'client' && (
              <div className="gm-form-group">
                <label>Client organisation</label>
                <select value={form.clientId} onChange={e => setForm(p => ({ ...p, clientId: e.target.value }))}>
                  <option value="">— Select client —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="gm-btn gm-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editingUser ? 'Save changes' : 'Create user'}
            </button>
            <button className="gm-btn gm-btn-outline" onClick={() => { setShowForm(false); setError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* User table */}
      <div className="gm-table-wrap">
        <table className="gm-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Company / Client</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No users found</td></tr>
            )}
            {filtered.map(u => {
              const company = u.companyId ? companies.find(c => c.id === u.companyId) : null;
              const clientObj = u.clientId ? clients.find(c => c.id === u.clientId) : null;
              const displayLocation = company?.name || u.companyName || clientObj?.name || u.clientId || '—';
              return (
                <tr key={u.id} style={{ opacity: u.disabled ? 0.5 : 1 }}>
                  <td style={{ fontWeight: 600 }}>{u.displayName}</td>
                  <td style={{ fontSize: '0.82rem' }}>{u.email}</td>
                  <td>
                    <span className={`gm-action-assignee--${u.role}`}
                      style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.82rem', color: '#6b6b6b' }}>
                    {displayLocation}
                  </td>
                  <td>
                    {u.disabled
                      ? <span className="gm-pending-badge" style={{ background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' }}>Deactivated</span>
                      : <span className="gm-validated-badge">Active</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="gm-btn gm-btn-sm gm-btn-outline" onClick={() => openEdit(u)}>Edit</button>
                      {!u.disabled && (
                        <button className="gm-btn gm-btn-sm gm-btn-outline"
                          style={{ color: '#dc2626', borderColor: '#fecaca' }}
                          onClick={() => handleDeactivate(u)}>
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 8 }}>
        {filtered.length} of {users.length} users
      </div>
    </div>
  );
}
